import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, X, Sparkles } from "lucide-react";
import { useApp, API } from "../context";

/**
 * ProfileCompletionToast, a soft, bottom-right nudge shown to any signed-in
 * user whose profile is missing a username, nickname, or sticker. Re-appears
 * every 24h after dismissal until the profile is complete. This is
 * intentionally separate from the HomePage `ProfileCompletionNudge` banner
 * (which only targets kid/solo-explorer `age/grade` completion), this toast
 * nags across ALL pages and all account types, keyed off the sticker/nickname
 * we actually need for leaderboards and the Header avatar.
 *
 * Behaviour:
 *   • Hidden for unauth / demo users
 *   • Hidden on setup / auth / legal pages (so we don't nag mid-flow)
 *   • 24h snooze via localStorage per user
 *   • Non-blocking, always dismissible
 */

const DISMISS_KEY_PREFIX = "iw_profile_toast_snoozed_until_";
const COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24h
const SUPPRESS_PATHS = [
  "/profile/setup",
  "/profile/edit",
  "/profile/avatar",
  "/login",
  "/signup",
  "/waitlist",
  "/legal",
];

const ProfileCompletionToast = () => {
  const { user } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [open, setOpen] = useState(false);

  const loadProfile = useCallback(async () => {
    if (!user || user.isDemo) return;
    try {
      const r = await axios.get(`${API}/profile/${user.id}`);
      setProfile(r.data || {});
    } catch (_) {
      setProfile({}); // treat as incomplete
    }
  }, [user]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  useEffect(() => {
    if (!user || user.isDemo) { setOpen(false); return; }
    // Admins / super_admins aren't forced through the 3-step flow, skip.
    if (user.role === "admin" || user.role === "super_admin") { setOpen(false); return; }
    if (SUPPRESS_PATHS.some((p) => location.pathname.startsWith(p))) { setOpen(false); return; }
    if (!profile) return;

    const hasUsername = !!profile.username;
    const hasNickname = !!profile.nickname;
    const hasSticker = !!profile.sticker;
    const complete = hasUsername && hasNickname && hasSticker;
    if (complete) { setOpen(false); return; }

    let snoozedUntil = 0;
    try {
      const raw = localStorage.getItem(`${DISMISS_KEY_PREFIX}${user.id}`);
      snoozedUntil = raw ? Number(raw) : 0;
    } catch (_) {}
    if (Date.now() < snoozedUntil) { setOpen(false); return; }

    const t = setTimeout(() => setOpen(true), 1500);
    return () => clearTimeout(t);
  }, [user, profile, location.pathname]);

  const snooze = () => {
    try { localStorage.setItem(`${DISMISS_KEY_PREFIX}${user.id}`, String(Date.now() + COOLDOWN_MS)); } catch (_) {}
    setOpen(false);
  };

  const goFinish = () => {
    snooze();
    navigate("/profile/setup");
  };

  if (!open || !user || user.isDemo || !profile) return null;

  // Prefer the canonical `profile_completion_missing` array shipped by
  // the unified `compute_profile_completion` helper (Solo/Parent/Kid all
  // populate it) so the toast uses the same labels as the HomePage
  // nudge, "Username", "Nickname", "Role / Grade level", "Sticker"
  // instead of the old lowercase trio. Fallback list keeps the toast
  // useful for legacy profiles that don't yet have the field.
  let missing = Array.isArray(profile.profile_completion_missing)
    ? [...profile.profile_completion_missing]
    : null;
  if (!missing) {
    missing = [];
    if (!profile.username) missing.push("Username");
    if (!profile.nickname) missing.push("Nickname");
    if (!profile.sticker) missing.push("Sticker");
  }
  let missingText;
  if (missing.length === 0) missingText = "Finish setting things up";
  else if (missing.length === 1) missingText = `Add your ${missing[0]} to hit 100%.`;
  else if (missing.length === 2) missingText = `Add your ${missing[0]} & ${missing[1]} to hit 100%.`;
  else {
    const head = missing.slice(0, -1).join(", ");
    missingText = `${missing.length} steps left: ${head}, and ${missing[missing.length - 1]}.`;
  }

  return (
    <div
      className="fixed bottom-4 right-4 z-40 max-w-xs w-[calc(100vw-2rem)] sm:w-80"
      data-testid="profile-completion-toast"
    >
      <div className="relative bg-gradient-to-br from-slate-900/95 to-slate-950/95 backdrop-blur-xl border border-amber-400/30 rounded-2xl shadow-2xl shadow-amber-500/10 p-4">
        <button
          type="button"
          onClick={snooze}
          className="absolute top-2 right-2 text-white/30 hover:text-white/60 p-1"
          aria-label="Snooze"
          data-testid="profile-toast-close"
        >
          <X size={14} />
        </button>
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-full bg-amber-400/15 border border-amber-400/30 flex items-center justify-center">
            <Sparkles size={18} className="text-amber-300" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white text-sm font-fredoka font-bold">Complete Your Profile</h3>
            <p className="text-white/50 text-xs mt-0.5 leading-snug">
              Pick a nickname and a sticker so friends can recognize you on leaderboards.
            </p>
            <p className="text-amber-300/80 text-[10px] mt-1" data-testid="profile-toast-missing">
              {missingText}
            </p>
            <div className="flex gap-2 mt-3">
              <button
                type="button"
                onClick={goFinish}
                className="flex-1 px-3 py-2 rounded-lg bg-gradient-to-r from-amber-400 to-yellow-400 text-black text-xs font-bold hover:from-amber-300 hover:to-yellow-300 transition-all flex items-center justify-center gap-1.5"
                data-testid="profile-toast-finish"
              >
                <CheckCircle2 size={12} /> Finish Now
              </button>
              <button
                type="button"
                onClick={snooze}
                className="px-3 py-2 rounded-lg border border-white/10 bg-white/5 text-white/50 text-xs font-bold hover:bg-white/10 transition-all"
                data-testid="profile-toast-later"
              >
                Later
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletionToast;
